import { useAtomValue } from 'jotai'
import * as React from 'react'
import { shallowEqual } from 'react-redux'
import { useNavigate } from 'react-router'

import { currentUserAtom } from '../../domains/user/AtomUsers'
import { STATUS } from '../../share/Constant'
import { isAdmin, isOwner } from '../../share/utility'
import { exportParticipantsToCsv } from '../../utils/csv'
import EventDisplay from '../common/EventDisplay'
import ConfirmationDialog from '../dialog/ConfirmationDialog'
import RegistrationConfirmationDialog from '../dialog/RegistrationConfirmationDialog'
import UnregistrationConfirmationDialog from '../dialog/UnregistrationConfirmationDialog'
import GroupSessionForm from '../forms/GroupSessionForm'
import useGroupSessionBooking from '../hooks/userGroupSessionBooking'
import { useParticipants } from '../hooks/useUsers'

const GroupSessionCard = ({ session, updateHook, deleteHook, mode = 'detailed', sx = {} }) => {
  const navigate = useNavigate()
  const user = useAtomValue(currentUserAtom)

  const {
    registerAction,
    unregisterAction,
    deleteAction,
    editAction,
    registerDialogOpen,
    editDialogOpen,
    submitting,
    unregisterDialogOpen,
    deleteDialogOpen,
    loading,
    editFormData,
    onFormDataChange,
    editFormErrors,
    selfRegister,
    sessionData,
  } = useGroupSessionBooking(session, updateHook, deleteHook, mode)

  const sessionUrl = `/events/sessions/${session.resourceId}`

  const { users: participantUsers } = useParticipants(session?.participants || [])

  const actions = (() => {
    const editable = isAdmin(user) || isOwner(user, session)
    const actions = {
      viewable: false,
      editable: editable && session.status !== STATUS.COMPLETED,
      unregisterable: (editable || selfRegister) && session.participants?.length > 0 && session.status !== STATUS.COMPLETED,
      deleteable: editable && session.status !== STATUS.COMPLETED,
      onRegister: registerAction.dialogOpen,
      onEdit: editAction.dialogOpen,
      onUnregister: unregisterAction.dialogOpen,
      onDelete: deleteAction.dialogOpen,
      onView: () => navigate(sessionUrl),
      exportable: editable,
      onExport: () =>
        exportParticipantsToCsv(participantUsers, session?.participants || [], {
          filenameBase: session?.title,
          date: session?.date,
        }),
    }

    actions.enabled = actions.editable || actions.unregisterable || actions.deleteable || actions.viewable || actions.exportable
    return actions
  })()

  const share = {
    enabled: true,
    title: `Group Session: ${session.title}`,
    text: `Group Session: ${session.title} on BookingOnSnow!`,
    url: window.location.origin + sessionUrl + '?focus=true',
  }

  return (
    <>
      <EventDisplay user={user} mode={mode} actions={actions} event={sessionData} link={sessionUrl} sx={sx} share={share} />

      {/* Registration Confirmation Dialog */}
      {registerDialogOpen && (
        <RegistrationConfirmationDialog
          session={session}
          open={registerDialogOpen}
          user={user}
          actions={{
            cancel: { label: 'Cancel', onClick: registerAction.dialogClose },
            confirm: { label: 'Confirm Booking', onClick: registerAction.do, submitting: loading },
          }}
        />
      )}

      {/* Edit Dialog */}
      {editDialogOpen && (
        <ConfirmationDialog
          open={editDialogOpen}
          title="Edit Group Session"
          content=""
          maxWidth="md"
          actions={{
            cancel: { label: 'Cancel', onClick: editAction.dialogClose },
            confirm: { label: 'Save Changes', onClick: editAction.do, color: 'info', variant: 'contained', submitting: submitting },
          }}
        >
          {editFormData && <GroupSessionForm formData={editFormData} formErrors={editFormErrors} onChange={onFormDataChange} />}
        </ConfirmationDialog>
      )}

      {/* Unregister Dialog */}
      {unregisterDialogOpen && (
        <UnregistrationConfirmationDialog
          open={unregisterDialogOpen}
          user={user}
          session={session}
          selfRegister={selfRegister}
          actions={{
            cancel: {
              label: 'Cancel',
              onClick: unregisterAction.dialogClose,
            },
            confirm: {
              label: 'Confirm Unregister',
              onClick: unregisterAction.do,
              color: 'error',
              variant: 'contained',
            },
          }}
        />
      )}

      {/* Delete Dialog */}
      {deleteDialogOpen && (
        <ConfirmationDialog
          open={deleteDialogOpen}
          title="Confirm Delete"
          content="Are you sure you want to delete this session? This action cannot be undone."
          actions={{
            cancel: { label: 'Cancel', onClick: deleteAction.dialogClose },
            confirm: { label: 'Confirm', onClick: deleteAction.do, color: 'error', variant: 'contained' },
          }}
        />
      )}
    </>
  )
}

export default React.memo(GroupSessionCard, (prevProps, nextProps) => {
  return shallowEqual(prevProps.session, nextProps.session)
})
